import React, { useEffect, useState, useRef } from "react";
import IntlMessages from "util/IntlMessages";
import { Card, Table, Row, Col, Button, Tooltip, Dropdown, Menu, Form, Select, Result } from "antd";
import CustomPagination from "../../../components/CustomPagination";
import { DownloadOutlined, CalendarOutlined, LeftOutlined, RightOutlined, CaretDownOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminPDTReport,
  downloadPDTReport,
  approvePDTReport,
  submitPDTReport,
} from "../../../appRedux/actions/ProductDevelopmentTimeline";
import { getRole, showMessage } from "../../../helpers";
import ReportActionButtons from "../../../components/ReportActionButtons";
import "./style.css";
import LoadingBar from "../../../components/LoadingBar";

const CustomFilterDropdown = ({
  options,
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  placeholder = "Search..."
}) => (
  <div style={{ padding: 8, width: 280 }}>
    <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
      <Select
        mode="multiple"
        placeholder={placeholder}
        value={selectedKeys}
        onChange={setSelectedKeys}
        showSearch
        style={{
          width: '100%',
          minHeight: 20,
          marginBottom: 0,
          display: 'block'
        }}
        filterOption={(input, option) =>
          option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        maxTagCount="responsive"
        allowClear={false}
        dropdownRender={menu => (
          <>
            {menu}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: 8,
              padding: '0 8px'
            }}>
              <Button
                size="small"
                onClick={() => {
                  clearFilters && clearFilters();
                  confirm && confirm();
                }}
                style={{ flex: 1 }}
              >
                Reset
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={() => confirm && confirm()}
                style={{ flex: 1 }}
              >
                Apply
              </Button>
            </div>
          </>
        )}
      >
        {options.map(option => (
          <Select.Option key={option.value} value={option.value}>
            {option.text}
          </Select.Option>
        ))}
      </Select>
    </div>
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
      <Button
        size="small"
        style={{ flex: 1 }}
        onClick={() => {
          clearFilters && clearFilters();
          confirm && confirm();
        }}
      >
        Reset
      </Button>
      <Button
        type="primary"
        size="small"
        style={{ flex: 1 }}
        onClick={() => confirm && confirm()}
      >
        Apply
      </Button>
    </div>
  </div>
);

const PDTReport = (props) => {
  const { isLoaded, pdtReportData, reportError } = useSelector(({ pdt }) => pdt);
  const dispatch = useDispatch();
  const fetchAbortRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });
  const [requestObj, setRequestObj] = useState({
    limit: 50,
    offset: 0,
  });

  const [quarterDropdown, setQuarterDropdown] = useState([]);
  const [quarterDropdownCurrent, setQuarterDropdownCurrent] = useState("Loading...");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [yearDropdownVisible, setYearDropdownVisible] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  // User role states
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserStaff, setIsUserStaff] = useState(false);
  const [isHistoricQuarter, setIsHistoricQuarter] = useState(false);
  const [reportStatus, setReportStatus] = useState("");
  const [messageCount, setMessageCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    company: null,
    product: null,
    stage: null,
    product_status: null,
  });


  const isDiagnosticAdmin = getRole() === "DIAGNOSTIC_ADMIN" || getRole() === "DIAGNOSTIC_REVIEWER";

  // Initialize user roles
  useEffect(() => {
    const userRole = (getRole() || "").toUpperCase();
    setIsUserAdmin(userRole === "ADMIN");
    setIsUserStaff(userRole === "STAFF");
  }, []);

  // Generate available years (current year and 10 years before)
  useEffect(() => {
    const currentYearNum = new Date().getFullYear();
    const years = [];
    for (let i = currentYearNum - 10; i <= currentYearNum; i++) {
      years.push(i);
    }
    setAvailableYears(years);
  }, []);

  // Quarter data mapping
  const quarterMap = [
    { id: 1, label: "Quarter 1", range: "Jan to Mar", shortRange: "Jan-Mar" },
    { id: 2, label: "Quarter 2", range: "Apr to Jun", shortRange: "Apr-Jun" },
    { id: 3, label: "Quarter 3", range: "Jul to Sep", shortRange: "Jul-Sep" },
    { id: 4, label: "Quarter 4", range: "Oct to Dec", shortRange: "Oct-Dec" }
  ];

  const normalizeQuarter = (q) => {
    if (!q) return q;
    // Handle strings like "Jan to March 2025" => "Jan-Mar 2025"
    if (q.includes("to")) {
      const yearMatch = q.match(/\d{4}/);
      const year = yearMatch ? yearMatch[0] : new Date().getFullYear();
      if (q.includes("Jan")) return `Jan-Mar ${year}`;
      if (q.includes("Apr")) return `Apr-Jun ${year}`;
      if (q.includes("Jul")) return `Jul-Sep ${year}`;
      if (q.includes("Oct")) return `Oct-Dec ${year}`;
    }
    // Possibly already in short form like "Jan-Mar 2025"
    if (q.includes("Jan") && q.includes("Mar")) return q.replace(/Jan.*?Mar/, "Jan-Mar");
    if (q.includes("Apr") && q.includes("Jun")) return q.replace(/Apr.*?Jun/, "Apr-Jun");
    if (q.includes("Jul") && q.includes("Sep")) return q.replace(/Jul.*?Sep/, "Jul-Sep");
    if (q.includes("Oct") && q.includes("Dec")) return q.replace(/Oct.*?Dec/, "Oct-Dec");
    return q;
  };

  // Utility to remove null/undefined keys
  const cleanObject = (obj) => {
    const cleaned = Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v !== null && v !== undefined)
    );
    if (cleaned.quarter) {
      cleaned.quarter = normalizeQuarter(cleaned.quarter);
    }
    return cleaned;
  };

  // SINGLE source of fetching: watch requestObj. This runs on mount once (initial requestObj),
  // and whenever requestObj changes (pagination, filters, quarter, etc.).
  useEffect(() => {
    dispatch(getAdminPDTReport(cleanObject(requestObj)));
  }, [requestObj.limit, requestObj.offset, requestObj.quarter, requestObj.partner_id, requestObj.product_id, requestObj.stages, requestObj.status]);

  const handleRetry = () => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    dispatch(getAdminPDTReport(cleanObject(requestObj), null, controller.signal));
  };

  // Update UI when API response arrives
  useEffect(() => {
    if (isLoaded) {
      const {
        quarter_order,
        results,
        count,
        quarter_dropdown,
        filters,
        current_quarter
      } = pdtReportData;

      const {
        partner_filter = [],
        product_filter = [],
        stage_filter = [],
        status_filter = [],
      } = filters || {};

      let dataCols = [
        {
          title: "Company Name",
          dataIndex: "company",
          key: "company",
          render: (name) => <span className="text-capitalize">{name}</span>,
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <CustomFilterDropdown
              options={partner_filter.map((partner) => ({
                text: partner.company_name.replace(/(^\w{1})|(\s{1}\w{1})/g, (m) => m.toUpperCase()),
                value: partner.partner_id,
              }))}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              confirm={confirm}
              clearFilters={clearFilters}
              placeholder="Search companies..."
            />
          ),
          filteredValue: selectedFilters.company || null
        },
        {
          title: "Product",
          dataIndex: "product",
          key: "product",
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <CustomFilterDropdown
              options={product_filter.map((product) => ({
                text: product.product_name,
                value: product.product_id,
              }))}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              confirm={confirm}
              clearFilters={clearFilters}
              placeholder="Search products..."
            />
          ),
          filteredValue: selectedFilters.product || null
        },
        {
          title: "Stage",
          dataIndex: "stage",
          key: "stage",
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <CustomFilterDropdown
              options={stage_filter.map((stage) => ({ text: stage, value: stage }))}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              confirm={confirm}
              clearFilters={clearFilters}
              placeholder="Search stages..."
            />
          ),
          filteredValue: selectedFilters.stage || null
        },
        {
          title: "Status",
          dataIndex: "product_status",
          key: "product_status",
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <CustomFilterDropdown
              options={status_filter.map((status) => ({ text: status, value: status }))}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              confirm={confirm}
              clearFilters={clearFilters}
              placeholder="Search status..."
            />
          ),
          filteredValue: selectedFilters.product_status || null
        },
      ];

      if (quarter_order && quarter_order.length) {
        quarter_order.forEach((value, index) => {
          dataCols.push({
            title: value,
            key: index,
            children: [
              {
                title: "Start Date",
                dataIndex: "start_date_" + value,
                key: "start_date_" + value,
                width: 120,
              },
              {
                title: "End Date",
                dataIndex: "end_date_" + value,
                key: "end_date_" + value,
                width: 120,
              },
            ],
          });
        });
      }

      setColumns(dataCols);
      setRows(results || []);
      setTotal(count || 0);

      const formattedQuarterDropdown = (quarter_dropdown || []).map(normalizeQuarter);
      setQuarterDropdown(formattedQuarterDropdown);

      // Set the UI current quarter from API (but DO NOT trigger a refetch here)
      if (quarterDropdownCurrent === "Loading..." && current_quarter) {
        setQuarterDropdownCurrent(normalizeQuarter(current_quarter));
      }
    }
  }, [isLoaded, pdtReportData, isDiagnosticAdmin]);

  const handleTableChange = (paginationInfo, filters) => {
    const { company, product, stage, product_status } = filters;

    setSelectedFilters({
      company: company || null,
      product: product || null,
      stage: stage || null,
      product_status: product_status || null,
    });

    // Reset pagination & offset when filters change
    setPagination(prev => ({ ...prev, current: 1 }));

    setRequestObj((prev) => {
      const updated = {
        ...prev,
        quarter: quarterDropdownCurrent,
        offset: 0 // reset offset
      };

      if (company?.length) updated.partner_id = company;
      else delete updated.partner_id;

      if (product?.length) updated.product_id = product;
      else delete updated.product_id;

      if (stage?.length) updated.stages = stage;
      else delete updated.stages;

      if (product_status?.length) updated.status = product_status;
      else delete updated.status;

      return updated;
    });
  };

  // When user changes quarter from the UI, update requestObj (which will trigger the single fetch)
  const changeQuarter = (quarter) => {
    const normalized = normalizeQuarter(quarter);
    setQuarterDropdownCurrent(normalized);
    setDropdownVisible(false);

    // Reset pagination when quarter changes
    setPagination(prev => ({ ...prev, current: 1 }));
    setRequestObj(prev => ({ ...prev, quarter: normalized, offset: 0 }));
  };

  // Action handlers for the ReportActionButtons component
  const handleSave = () => {
    showMessage("info", "Save functionality for PDT reports is not yet implemented");
  };

  const handleApprove = () => {
    const data = {
      partner_id: null,
      message: "PDT Report approved",
      is_approved: true,
    };

    dispatch(
      approvePDTReport(data, () => {
        showMessage("success", "PDT Report Approved!");
        dispatch(getAdminPDTReport(cleanObject(requestObj)));
      })
    );
  };

  const handleReject = () => {
    const data = {
      partner_id: null,
      message: "PDT Report rejected",
      is_approved: false,
    };

    dispatch(
      approvePDTReport(data, () => {
        showMessage("success", "PDT Report Rejected!");
        dispatch(getAdminPDTReport(cleanObject(requestObj)));
      })
    );
  };

  const handleDownload = () => {
    dispatch(
      downloadPDTReport(
        "pdt-report.csv",
        quarterDropdownCurrent,
        requestObj,
      )
    );
  };

  // Format quarter display
  const formatQuarterDisplay = (quarter) => {
    if (quarter === "Loading...") return "Loading...";

    if (quarter.includes("to")) return quarter;
    const match = quarter.match(/Q(\d+)\s+(\d{4})/);
    if (match) {
      const qNum = parseInt(match[1]);
      const year = match[2];
      const qData = quarterMap.find(q => q.id === qNum);
      if (qData) {
        return qData.range + " " + year;
      }
    }

    return quarter;
  };

  return (
    <div>
      <Row>
        <Col span={24}>
          <div className="gx-flex-row gx-align-items-center gx-justify-content-between">
            <h1 className="title gx-mb-4">
              <IntlMessages id="report.pdt" />
            </h1>
            <div className="gx-flex-row gx-align-items-center gx-mb-4">

              <ReportActionButtons
                quarterDropdownCurrent={quarterDropdownCurrent}
                onQuarterChange={changeQuarter}
                quarterDropdown={quarterDropdown}
                onSave={handleSave}
                onApprove={handleApprove}
                onReject={handleReject}
                onDownload={handleDownload}
                isUserAdmin={isUserAdmin}
                isUserStaff={isUserStaff}
                isHistoricQuarter={isHistoricQuarter}
                reportStatus={reportStatus}
                messageCount={messageCount}
                messages={messages}
                showSave={true}
                showApprove={true}
                showReject={true}
                showDownload={true}
                showQuarterDropdown={true}
                showSubmit={false}
                className="pdt-report-actions"
              />

            </div>
          </div>
        </Col>

        {!isLoaded ? (
          <LoadingBar loading={!isLoaded} />
        ) : reportError ? (
          <Col span={24}>
            <Result
              status="error"
              title="Failed to load PDT report"
              subTitle={reportError}
              extra={
                <Button type="primary" onClick={handleRetry}>
                  Retry
                </Button>
              }
            />
          </Col>
        ) : (
          <Table
            className="gx-table-responsive mpp-list-table"
            columns={columns.map(col => ({
              ...col,
              onCell: () => ({
                style: { textAlign: 'center', verticalAlign: 'middle' }
              })
            }))}
            dataSource={rows}
            rowKey={(record) =>
              [record.company, record.product, record.stage, record.product_status].join("|")
            }
            pagination={false}
            size="small"
            onChange={handleTableChange}
            bordered
            scroll={{ 
              x: 'max-content', 
              y: 'calc(100vh - 300px)'
            }}
            sticky= {{ offsetHeader: 0 }}
          />
        )}

        {/* Custom Pagination */}
        <CustomPagination
          current={pagination.current}
          total={total}
          pageSize={pagination.pageSize}
          showSizeChanger={true}
          pageSizeOptions={['10', '20', '50', '100']}
          onChange={(page, pageSize) => {
            setPagination({
              current: page,
              pageSize: pageSize,
            });

            // Calculate offset for API call
            const offset = (page - 1) * pageSize;

            // Update request object for API call
            setRequestObj(prev => ({
              ...prev,
              limit: pageSize,
              offset: offset,
            }));
          }}
        />
      </Row>
    </div>
  );
};

export default PDTReport;